import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Box, Heading, Flex, Text, HStack, VStack, Tag, Badge, Avatar, Button,
  useToast, Spinner, Menu, MenuButton, MenuList, MenuItem, useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Input, Select, Textarea,
  Stat, StatLabel, StatNumber, SimpleGrid, IconButton, Divider, Progress,
  Editable, EditablePreview, EditableInput,
} from "@chakra-ui/react";
import { ChevronDownIcon, AddIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import KanbanBoard from "../Tasks/KanbanBoard";
import TaskDetailDrawer from "../Tasks/TaskDetailDrawer";

const DEPT_STATUSES = ["not_started", "in_progress", "waiting_approval", "blocked", "delivered"];
const DEPT_STATUS_COLORS = {
  not_started: "gray", in_progress: "blue", waiting_approval: "purple",
  blocked: "red", delivered: "green",
};
const PRIORITIES = ["low", "normal", "high", "urgent"];
const TASK_STATUSES = ["todo", "in_progress", "review", "done"];

export default function DepartmentWorkspace() {
  const { id: projectId, deptId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [dept, setDept] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const createTask = useDisclosure();
  const taskDrawer = useDisclosure();
  const briefModal = useDisclosure();
  const submitModal = useDisclosure();
  const reviewModal = useDisclosure();
  const [reviewMode, setReviewMode] = useState("approve");
  const [submitNote, setSubmitNote] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [openTaskId, setOpenTaskId] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", priority: "normal", status: "todo", dueDate: "", assigneeId: "" });
  const me = localStorage.getItem("userID");

  async function load() {
    setLoading(true);
    try {
      const [d, t, u] = await Promise.all([
        api(`/project-departments/${deptId}`),
        api(`/project-departments/${deptId}/tasks`),
        api("/chat/users").catch(() => ({ users: [] })),
      ]);
      setDept(d.department);
      setTasks(t.tasks || []);
      setAllUsers(u.users || []);
    } catch (e) {
      toast({ title: e.message, status: "error" });
      if (e.status === 403 || e.status === 404) navigate(`/projects/${projectId}`);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [deptId]);

  useShellHeader(
    dept ? {
      title: `${dept.team?.name || "Department"}`,
      breadcrumb: `Projects / ${dept.project?.name || "—"} / ${dept.team?.name || "—"}`,
    } : { title: "Loading..." },
    [dept?.team?.name, dept?.project?.name]
  );

  async function approvalAction(path, body) {
    try {
      const r = await api(`/project-departments/${deptId}/${path}`, { method: "POST", body: JSON.stringify(body) });
      setDept(r.department);
      toast({ title: "Done", status: "success" });
      submitModal.onClose();
      reviewModal.onClose();
      setSubmitNote("");
      setReviewNote("");
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function patch(body) {
    try {
      const r = await api(`/project-departments/${deptId}`, { method: "PATCH", body: JSON.stringify(body) });
      setDept(r.department);
      toast({ title: "Updated", status: "success", duration: 1200 });
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function createNewTask() {
    if (!taskForm.title.trim()) return;
    try {
      const body = {
        title: taskForm.title,
        description: taskForm.description,
        status: taskForm.status,
        priority: taskForm.priority,
        dueDate: taskForm.dueDate || null,
        assignees: taskForm.assigneeId ? [taskForm.assigneeId] : [],
      };
      const r = await api(`/project-departments/${deptId}/tasks`, { method: "POST", body: JSON.stringify(body) });
      setTasks((prev) => [r.task, ...prev]);
      toast({ title: "Task created", status: "success" });
      createTask.onClose();
      setTaskForm({ title: "", description: "", priority: "normal", status: "todo", dueDate: "", assigneeId: "" });
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  if (loading || !dept) return <Spinner />;

  const isAm = localStorage.getItem("role") === "SUPER_ADMIN" ||
    String(dept.project?.owner?._id || dept.project?.owner) === String(me);
  const isPoc = dept.poc && String(dept.poc._id) === String(me);
  const isManager = isAm || isPoc;

  const overdue = tasks.filter((t) => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "done").length;
  const done = tasks.filter((t) => t.status === "done").length;
  const progress = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  return (
    <Box>
      <Flex mb={4} align="center" gap={2}>
        <IconButton aria-label="back" icon={<ArrowBackIcon />} variant="ghost" onClick={() => navigate(`/projects/${projectId}`)} />
      </Flex>

      <Flex bg="white" p={6} rounded="md" shadow="sm" mb={6} align="start" borderTop="4px solid" borderColor={dept.team?.color || "blue.400"}>
        <Box flex="1">
          <HStack mb={2} spacing={3}>
            <Heading size="md">{dept.team?.name}</Heading>
            <Tag>{dept.team?.key}</Tag>
            <Menu>
              <MenuButton as={Button} size="sm" rightIcon={<ChevronDownIcon />} colorScheme={DEPT_STATUS_COLORS[dept.status]}>
                {dept.status.replace("_", " ")}
              </MenuButton>
              <MenuList>
                {DEPT_STATUSES.map((s) => (
                  <MenuItem key={s} onClick={() => patch({ status: s })}>{s.replace("_", " ")}</MenuItem>
                ))}
              </MenuList>
            </Menu>
            {isManager && (
              <Menu>
                <MenuButton as={Button} size="sm" variant="outline" rightIcon={<ChevronDownIcon />}>
                  Priority: {dept.priority}
                </MenuButton>
                <MenuList>
                  {PRIORITIES.map((p) => (
                    <MenuItem key={p} onClick={() => patch({ priority: p })}>{p}</MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
          </HStack>
          <Text fontSize="sm" color="gray.600">
            <RouterLink to={`/projects/${projectId}`}>
              <Text as="span" color="blue.500" textDecoration="underline">{dept.project?.name}</Text>
            </RouterLink>
          </Text>
          {dept.brief && (
            <Box mt={3} p={3} bg="gray.50" rounded="md" fontSize="sm">
              <Text fontSize="xs" color="gray.500" mb={1} fontWeight={600}>BRIEF</Text>
              <Text whiteSpace="pre-wrap">{dept.brief}</Text>
              {isManager && (
                <Button size="xs" variant="link" mt={2} onClick={briefModal.onOpen}>Edit brief</Button>
              )}
            </Box>
          )}
        </Box>

        <VStack align="stretch" minW="220px" spacing={3} ml={6}>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>POC</Text>
            <HStack>
              <Avatar size="sm" name={dept.poc?.username || "—"} />
              <Box>
                <Text fontSize="sm" fontWeight={600}>{dept.poc?.username || "Unassigned"}</Text>
                <Text fontSize="xs" color="gray.500">{dept.poc?.designation || dept.poc?.email}</Text>
              </Box>
            </HStack>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>Deadline</Text>
            {isManager ? (
              <Input size="sm" type="date" value={dept.deadline ? dept.deadline.slice(0, 10) : ""}
                onChange={(e) => patch({ deadline: e.target.value || null })} />
            ) : (
              <Text fontSize="sm">{dept.deadline ? new Date(dept.deadline).toLocaleDateString() : "—"}</Text>
            )}
          </Box>
        </VStack>
      </Flex>

      <Flex bg="white" p={4} rounded="md" shadow="sm" mb={6} align="center" gap={3}>
        <Box flex="1">
          <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" mb={1}>Approval</Text>
          {dept.status === "delivered" ? (
            <Text fontSize="sm" color="green.600" fontWeight={600}>✓ Approved & delivered</Text>
          ) : dept.status === "waiting_approval" ? (
            <Text fontSize="sm" color="purple.600" fontWeight={600}>Waiting for AM review</Text>
          ) : (
            <Text fontSize="sm" color="gray.600">Submit when this department's deliverables are ready.</Text>
          )}
        </Box>
        {dept.status !== "delivered" && dept.status !== "waiting_approval" && (
          <Button size="sm" colorScheme="purple" onClick={submitModal.onOpen}>Submit for review</Button>
        )}
        {dept.status === "waiting_approval" && isAm && (
          <HStack>
            <Button size="sm" variant="outline" onClick={() => { setReviewMode("changes"); reviewModal.onOpen(); }}>Request changes</Button>
            <Button size="sm" colorScheme="green" onClick={() => { setReviewMode("approve"); reviewModal.onOpen(); }}>Approve</Button>
          </HStack>
        )}
      </Flex>

      {dept.approvals && dept.approvals.length > 0 && (
        <Box bg="white" p={4} rounded="md" shadow="sm" mb={6}>
          <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" mb={3}>Approval history</Text>
          <VStack align="stretch" spacing={3} divider={<Divider />}>
            {[...dept.approvals].reverse().map((a) => (
              <Box key={a._id}>
                <HStack mb={1}>
                  <Text fontSize="sm" fontWeight={600}>{a.submittedBy?.username || "—"}</Text>
                  <Text fontSize="xs" color="gray.500">submitted on {new Date(a.submittedAt).toLocaleString()}</Text>
                </HStack>
                {a.submitterNote && <Text fontSize="sm" color="gray.700" mb={2} pl={2} borderLeft="2px solid" borderColor="gray.200">{a.submitterNote}</Text>}
                {a.reviewedAt && (
                  <Box mt={2} p={2} bg={a.decision === "approved" ? "green.50" : "orange.50"} rounded="md">
                    <HStack mb={1}>
                      <Badge colorScheme={a.decision === "approved" ? "green" : "orange"}>{a.decision === "approved" ? "Approved" : "Changes requested"}</Badge>
                      <Text fontSize="xs" color="gray.600">by {a.reviewedBy?.username || "—"} on {new Date(a.reviewedAt).toLocaleString()}</Text>
                    </HStack>
                    {a.reviewerNote && <Text fontSize="sm" color="gray.700">{a.reviewerNote}</Text>}
                  </Box>
                )}
                {!a.reviewedAt && <Text fontSize="xs" color="purple.600" fontStyle="italic">Awaiting review</Text>}
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={6}>
        <Stat bg="white" p={4} rounded="md" shadow="sm"><StatLabel>Progress</StatLabel><StatNumber>{progress}%</StatNumber>
          <Progress value={progress} size="xs" mt={2} colorScheme={progress === 100 ? "green" : "blue"} rounded="full" /></Stat>
        <Stat bg="white" p={4} rounded="md" shadow="sm"><StatLabel>Tasks</StatLabel><StatNumber>{tasks.length}</StatNumber></Stat>
        <Stat bg="white" p={4} rounded="md" shadow="sm"><StatLabel>Done</StatLabel><StatNumber color="green.500">{done}</StatNumber></Stat>
        <Stat bg="white" p={4} rounded="md" shadow="sm">
          <StatLabel>Overdue</StatLabel>
          <StatNumber color={overdue > 0 ? "red.500" : "gray.500"}>{overdue}</StatNumber>
        </Stat>
      </SimpleGrid>

      <Box bg="white" rounded="md" shadow="sm" p={4}>
        <HStack justify="space-between" mb={4}>
          <Heading size="sm">Internal tasks</Heading>
          <Button leftIcon={<AddIcon />} size="sm" colorScheme="blue" onClick={createTask.onOpen}>
            Add Task
          </Button>
        </HStack>
        {tasks.length === 0 ? (
          <Text color="gray.500" textAlign="center" py={6}>
            No tasks yet — click "Add Task" to start.
          </Text>
        ) : (
          <KanbanBoard
            tasks={tasks}
            onChange={setTasks}
            onTaskClick={(t) => { setOpenTaskId(t._id); taskDrawer.onOpen(); }}
          />
        )}
      </Box>

      <Modal isOpen={createTask.isOpen} onClose={createTask.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New task in {dept.team?.name}</ModalHeader><ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}><FormLabel>Title</FormLabel>
              <Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} autoFocus />
            </FormControl>
            <FormControl mb={3}><FormLabel>Description</FormLabel>
              <Textarea rows={3} value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} />
            </FormControl>
            <SimpleGrid columns={3} spacing={3} mb={3}>
              <FormControl><FormLabel>Status</FormLabel>
                <Select value={taskForm.status} onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}>
                  {TASK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </FormControl>
              <FormControl><FormLabel>Priority</FormLabel>
                <Select value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </Select>
              </FormControl>
              <FormControl><FormLabel>Due date</FormLabel>
                <Input type="date" value={taskForm.dueDate} onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })} />
              </FormControl>
            </SimpleGrid>
            <FormControl><FormLabel>Assign to</FormLabel>
              <Select placeholder="Unassigned" value={taskForm.assigneeId} onChange={(e) => setTaskForm({ ...taskForm, assigneeId: e.target.value })}>
                {(dept.members || []).length > 0
                  ? dept.members.map((m) => <option key={m._id} value={m._id}>{m.username}</option>)
                  : allUsers.map((u) => <option key={u._id} value={u._id}>{u.username}</option>)
                }
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={createTask.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={createNewTask} isDisabled={!taskForm.title.trim()}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={briefModal.isOpen} onClose={briefModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit brief</ModalHeader><ModalCloseButton />
          <ModalBody>
            <Textarea rows={6} defaultValue={dept.brief} onBlur={(e) => e.target.value !== dept.brief && patch({ brief: e.target.value })} />
          </ModalBody>
          <ModalFooter><Button onClick={briefModal.onClose}>Done</Button></ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={submitModal.isOpen} onClose={submitModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Submit {dept.team?.name} for review</ModalHeader><ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>Add a note for the Account Manager (optional).</Text>
            <Textarea rows={4} value={submitNote} onChange={(e) => setSubmitNote(e.target.value)}
              placeholder="What's been delivered? Any context the AM should know?" />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={submitModal.onClose}>Cancel</Button>
            <Button colorScheme="purple" onClick={() => approvalAction("submit", { note: submitNote })}>Submit for review</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={reviewModal.isOpen} onClose={reviewModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{reviewMode === "approve" ? "Approve & deliver" : "Request changes"}</ModalHeader><ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              {reviewMode === "approve"
                ? "Mark this department's work as delivered. Optional note."
                : "Tell the team what needs to change. A note is required."}
            </Text>
            <Textarea rows={4} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)}
              placeholder={reviewMode === "approve" ? "Optional note..." : "What needs to change?"} />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={reviewModal.onClose}>Cancel</Button>
            <Button
              colorScheme={reviewMode === "approve" ? "green" : "orange"}
              isDisabled={reviewMode === "changes" && !reviewNote.trim()}
              onClick={() => approvalAction(reviewMode === "approve" ? "approve" : "request-changes", { note: reviewNote })}
            >
              {reviewMode === "approve" ? "Approve & deliver" : "Request changes"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <TaskDetailDrawer
        isOpen={taskDrawer.isOpen}
        onClose={() => { taskDrawer.onClose(); setOpenTaskId(null); load(); }}
        taskId={openTaskId}
        allUsers={dept.members?.length ? dept.members : allUsers}
        onChanged={(updated) => {
          if (updated == null) {
            setTasks((prev) => prev.filter((t) => t._id !== openTaskId));
          } else {
            setTasks((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
          }
        }}
      />
    </Box>
  );
}
